var _excluded = ["children", "disableMotion", "animationDuration", "animateOpacity"],
  _excluded2 = ["htmlProps"],
  _excluded3 = ["children", "className"],
  _excluded4 = ["htmlProps"],
  _excluded5 = ["render"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import { FaChevronDown, FaAngleDown } from 'react-icons/fa';
import { useThemedValueSwitch } from '../../pp2';
import { forwardRef, omitThemingProps, StylesProvider, tqc, useMultiStyleConfig, useStyles } from '../../system';
import { createContext, cx, runIfFn, __DEV__ } from '../../utils';
import { IconButton } from '../button';
import { LinkButton, Box } from '../layout';
import { Collapse } from '../transition';
import { AccordionContextProvider, useAccordion, useAccordionContext, useAccordionItem } from './use-accordion';
/**
 * The wrapper that provides context and focus management
 * for all accordion items.
 *
 * It wraps all accordion items in a `div` for better grouping.
 */
export var Accordion = forwardRef((_ref, ref) => {
  var {
      children,
      disableMotion = false,
      animationDuration = 300,
      animateOpacity = true
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  var styles = useMultiStyleConfig('Accordion', props);
  var ownProps = omitThemingProps(props);
  var _useAccordion = useAccordion(ownProps),
    {
      htmlProps
    } = _useAccordion,
    context = _objectWithoutProperties(_useAccordion, _excluded2);
  var ctx = React.useMemo(() => _objectSpread(_objectSpread({}, context), {}, {
    disableMotion,
    animationDuration,
    animateOpacity
  }), [context, disableMotion, animationDuration, animateOpacity]);
  return /*#__PURE__*/React.createElement(AccordionContextProvider, {
    value: ctx
  }, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(tqc.div, _extends({
    ref: ref
  }, htmlProps, {
    className: cx('tqc-accordion', props.className)
  }), children)));
});
if (__DEV__) {
  Accordion.displayName = 'Accordion';
}
var [AccordionItemContextProvider, useAccordionItemContext] = createContext({
  name: 'AccordionItemContext',
  errorMessage: 'useAccordionItemContext: `context` is undefined. Seems you forgot to wrap the accordion item parts in `<AccordionItem />` '
});
/**
 * AccordionItem is a single accordion that provides the open-close
 * behavior when the accordion button is clicked.
 *
 * It also provides context for the accordion button and panel.
 */
export var AccordionItem = forwardRef((props, ref) => {
  var {
      children,
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded3);
  var _useAccordionItem = useAccordionItem(props),
    {
      htmlProps
    } = _useAccordionItem,
    context = _objectWithoutProperties(_useAccordionItem, _excluded4);
  var styles = useStyles();
  var containerStyles = _objectSpread(_objectSpread({}, styles.container), {}, {
    overflowAnchor: 'none'
  });
  var _context = React.useMemo(() => context, [context]);
  return /*#__PURE__*/React.createElement(AccordionItemContextProvider, {
    value: _context
  }, /*#__PURE__*/React.createElement(tqc.div, _extends({
    ref: ref
  }, htmlProps, {
    className: cx('tqc-accordion__item', className),
    __css: containerStyles,
    "data-open": context.isOpen
  }, rest), runIfFn(children, {
    isExpanded: !!context.isOpen,
    isDisabled: !!context.isDisabled
  })));
});
if (__DEV__) {
  AccordionItem.displayName = 'AccordionItem';
}

/**
 * React hook to get the state and actions of an accordion item
 */
export function useAccordionItemState() {
  var {
    isOpen,
    isDisabled,
    onClose,
    onOpen
  } = useAccordionItemContext();
  return {
    isOpen,
    onClose,
    isDisabled,
    onOpen
  };
}
/**
 * AccordionHeader is holds content that is always visible and the icon button that expands and Collapses an accordion item.
 * It must be a child of `AccordionItem`.
 *
 * Note 🚨: Each accordion header must be wrapped in an heading tag,
 * that is appropriate for the information architecture of the page.
 */
export var AccordionHeader = forwardRef((props, ref) => {
  var _styles$header$border, _styles$header, _styles$header$border2, _styles$header2, _styles$header$border3, _styles$header3;
  var {
    isOpen
  } = useAccordionItemContext();
  var styles = useStyles();
  var {
    disableMotion,
    animationDuration
  } = useAccordionContext();
  var headerStyles = _objectSpread(_objectSpread({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    transition: disableMotion ? undefined : "all ".concat(animationDuration, "ms"),
    outline: 0
  }, styles.header), {}, {
    borderBottomColor: isOpen ? (_styles$header$border = (_styles$header = styles.header) === null || _styles$header === void 0 ? void 0 : _styles$header.borderColor) !== null && _styles$header$border !== void 0 ? _styles$header$border : 'transparent' : 'transparent',
    borderBottomLeftRadius: isOpen ? 'none' : (_styles$header$border2 = (_styles$header2 = styles.header) === null || _styles$header2 === void 0 ? void 0 : _styles$header2.borderBottomLeftRadius) !== null && _styles$header$border2 !== void 0 ? _styles$header$border2 : undefined,
    borderBottomRightRadius: isOpen ? 'none' : (_styles$header$border3 = (_styles$header3 = styles.header) === null || _styles$header3 === void 0 ? void 0 : _styles$header3.borderBottomRightRadius) !== null && _styles$header$border3 !== void 0 ? _styles$header$border3 : undefined
  });
  return /*#__PURE__*/React.createElement(tqc.div, _extends({
    ref: ref,
    className: cx('tqc-accordion__header', props.className),
    "data-open": isOpen,
    __css: headerStyles
  }, props));
});
if (__DEV__) {
  AccordionHeader.displayName = 'AccordionHeader';
}
/**
 * Accordion panel that holds the content for each accordion.
 * It shows and hides based on the state login from the `AccordionItem`.
 *
 * When motion is enabled, it uses the `Collapse` component to animate its height.
 */
export var AccordionPanel = forwardRef((props, ref) => {
  var {
    disableMotion,
    animationDuration,
    animateOpacity
  } = useAccordionContext();
  var {
    getPanelProps,
    isOpen
  } = useAccordionItemContext();
  var panelProps = getPanelProps(props, ref);
  var _className = cx('tqc-accordion__panel', props.className);
  var styles = useStyles();
  if (!disableMotion) {
    delete panelProps.hidden;
  }
  var child = /*#__PURE__*/React.createElement(tqc.div, _extends({}, panelProps, {
    __css: styles.panel,
    className: _className
  }));
  return /*#__PURE__*/React.createElement(Collapse, {
    unmountOnExit: true,
    duration: animationDuration,
    in: isOpen,
    disableMotion: disableMotion,
    animateOpacity: animateOpacity
  }, child);
});
if (__DEV__) {
  AccordionPanel.displayName = 'AccordionPanel';
}
export var WalesAccordionToggleIcon = _ref2 => {
  var {
    isOpen
  } = _ref2;
  return /*#__PURE__*/React.createElement(Box, {
    width: "2rem",
    height: "2rem",
    display: "grid",
    justifyItems: "center",
    alignItems: "center",
    _before: {
      content: "''",
      width: '1.5rem',
      height: '.25rem',
      bg: 'currentColor',
      display: 'block',
      gridRow: '1 / 1',
      gridColumn: '1 / 1'
    },
    _after: {
      content: "''",
      opacity: isOpen ? '0' : '1',
      transition: 'opacity .2s ease',
      width: '.25rem',
      height: '1.5rem',
      bg: 'currentColor',
      display: 'block',
      gridRow: '1 / 1',
      gridColumn: '1 / 1'
    }
  });
};

/**
 * AccordionIconButton gives a visual cue of the open/close state of the accordion item and,
 * when activated, will toggle the modal.
 * When motion is enabled, it rotates `180deg` based on the open/close state.
 */
export var AccordionIconButton = forwardRef((props, ref) => {
  var {
    children,
    disableMotion: disableMotionProp
  } = props;
  var {
    isWales,
    isTQ
  } = useThemedValueSwitch();
  var {
    isOpen,
    getButtonProps,
    isDisabled
  } = useAccordionItemContext();
  var {
    disableMotion: disableMotionGroup,
    animationDuration
  } = useAccordionContext();
  var buttonProps = getButtonProps(props, ref);
  var styles = useStyles();
  var disableMotion = disableMotionProp || disableMotionGroup;
  var buttonStyles = _objectSpread(_objectSpread({
    fontSize: '1.25rem',
    transform: isOpen ? 'rotate(-180deg)' : undefined,
    transition: disableMotion ? undefined : "all ".concat(animationDuration, "ms"),
    transformOrigin: 'center'
  }, styles.button), props.sx);
  return isWales ? /*#__PURE__*/React.createElement(AccordionButton, _extends({
    "aria-label": isOpen ? 'Open' : 'Close' + ' accordion',
    ref: ref,
    render: _ref3 => {
      var {
        isOpen
      } = _ref3;
      return /*#__PURE__*/React.createElement(WalesAccordionToggleIcon, {
        isOpen: isOpen
      });
    }
  }, buttonProps)) : /*#__PURE__*/React.createElement(IconButton, _extends({
    ref: ref,
    isDisabled: isDisabled
  }, buttonProps, {
    sx: buttonStyles,
    "aria-label": isOpen ? 'Open' : 'Close' + ' accordion'
  }, props), children || isTQ ? /*#__PURE__*/React.createElement(FaAngleDown, {
    "aria-hidden": true
  }) : /*#__PURE__*/React.createElement(FaChevronDown, {
    "aria-hidden": true
  }));
});
if (__DEV__) {
  AccordionIconButton.displayName = 'AccordionIconButton';
}

/**
 * AccordionButton gives a textual cue of the open/close state of the accordion item and,
 * when activated, will toggle the modal.
 * The child render prop gives access to the open/close state.
 */

export var AccordionButton = forwardRef((props, ref) => {
  var {
      render
    } = props,
    rest = _objectWithoutProperties(props, _excluded5);
  var otherProps = rest;
  var {
    isOpen,
    getButtonProps,
    isDisabled
  } = useAccordionItemContext();
  var buttonProps = getButtonProps(otherProps, ref);
  var styles = useStyles();
  var buttonStyles = _objectSpread(_objectSpread({
    fontSize: '1.25rem',
    transition: 'all 150ms',
    transformOrigin: 'center'
  }, styles.button), rest.sx);
  return /*#__PURE__*/React.createElement(LinkButton, _extends({
    ref: ref,
    isDisabled: isDisabled
  }, buttonProps, {
    sx: buttonStyles
  }, rest), render && render({
    isOpen
  }));
});
if (__DEV__) {
  AccordionButton.displayName = 'AccordionButton';
}